/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleCustomerProposals } from 'src/components/TradeRule';
import TradeRuleCustomerProposalsPlaceholder from 'src/components/TradeRule/TradeRuleCustomerProposals/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getEmptyRetryVariables from './helpers/getEmptyRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const tradeRuleCustomerProposalsRouteQuery = graphql`
  query TradeRuleCustomerProposalsRoute_Query {
    viewer {
      ...TradeRuleCustomerProposals_viewer
    }
  }
`;

const tradeRuleCustomerProposalsRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({}),
});

/**
 * Renders `TradeRuleCustomerProposals` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule customer proposals route content.
 */
function TradeRuleCustomerProposalsPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleCustomerProposalsRouteQuery, queryRef);
  return <TradeRuleCustomerProposals {...routeProps} {...queryData} />;
}

TradeRuleCustomerProposalsPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleCustomerProposalsRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleCustomerProposals` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule customer proposals route content.
 */
function TradeRuleCustomerProposalsRouteRenderer({ routeProps, routeError = null }) {
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleCustomerProposalsRouteQuery,
    routeProps,
    { logLabel: 'TradeRuleCustomerProposalsRoute', getRetryVariablesFunc: getEmptyRetryVariables },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={TradeRuleCustomerProposalsPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleCustomerProposalsPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleCustomerProposals {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleCustomerProposalsRouteRenderer.propTypes = {
  routeProps: tradeRuleCustomerProposalsRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleCustomerProposals route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. Authentication is resolved by the
 * owning `CustomerLayout` route, not here. This route is not entity-keyed, so
 * no `key` is applied.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleCustomerProposals route content or loading state.
 */
export function tradeRuleCustomerProposalsRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleCustomerProposalsRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return <TradeRuleCustomerProposalsRouteRenderer routeProps={props} />;
}

export default TradeRuleCustomerProposalsRouteRenderer;
